import React, { useState, useMemo } from 'react';
import {
  PCI_DSS_2025_CONTROLS_CATALOG,
  getPciDssControlsByDomain,
  getPciDssControlDomains,
  type PciDssControl,
  type PciDssControlStatus,
} from '../../data/pciDss2025ControlsCatalog';
import { computePciDssControlsScore } from '../../utils/pciDssScore';
import { useI18n } from '../../i18n';
import { ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { componentScope } from '../../lib/cssScope'
import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'

const STATUS_OPTIONS: PciDssControlStatus[] = ['IMPLEMENTED', 'PARTIAL', 'NOT_IMPLEMENTED', 'N_A'];
const STATUS_LABELS: Record<PciDssControlStatus, string> = {
  IMPLEMENTED: 'Implemented',
  PARTIAL: 'Partial',
  NOT_IMPLEMENTED: 'Not Implemented',
  N_A: 'N/A',
};

interface PciDssControlsPanelProps {
  initialStatuses?: Record<string, PciDssControlStatus>;
  onSave?: (statuses: Record<string, PciDssControlStatus>) => Promise<void>;
  disabled?: boolean;
}

export default function PciDssControlsPanel({
  initialStatuses = {},
  onSave,
  disabled = false,
}: PciDssControlsPanelProps) {
  const { t, locale } = useI18n();
  const DOMAIN_ORDER = getPciDssControlDomains();
  const [statuses, setStatuses] = useState<Record<string, PciDssControlStatus>>(initialStatuses);
  React.useEffect(() => {
    if (initialStatuses && Object.keys(initialStatuses).length > 0) {
      setStatuses((prev) => ({ ...prev, ...initialStatuses }));
    }
  }, [initialStatuses]);
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(
    new Set([DOMAIN_ORDER[0] || 'Network'])
  );
  const [filterDomain, setFilterDomain] = useState<PciDssControl['domain'] | 'all'>('all');
  const [isSaving, setIsSaving] = useState(false);
  const handleStatusChange = (code: string, status: PciDssControlStatus | '') => {
    if (disabled) return;
    if (status === '') {
      setStatuses((prev) => {
        const next = { ...prev };
        delete next[code];
        return next;
      });
      return;
    }
    setStatuses((prev) => ({ ...prev, [code]: status }));
  };
  const handleSave = async () => {
    if (!disabled && !isSaving && onSave) {
      setIsSaving(true);
      try {
        await onSave(statuses);
      } finally {
        setIsSaving(false);
      }
    }
  };
  const toggleDomain = (d: string) =>
    setExpandedDomains((prev) => {
      const n = new Set(prev);
      n.has(d) ? n.delete(d) : n.add(d);
      return n;
    });
  const controlsByDomain = useMemo(
    () =>
      filterDomain === 'all'
        ? DOMAIN_ORDER.flatMap((d) => getPciDssControlsByDomain(d))
        : getPciDssControlsByDomain(filterDomain),
    [filterDomain, DOMAIN_ORDER]
  );
  const stats = useMemo(() => {
    const { score, breakdown } = computePciDssControlsScore(statuses);
    return { total: PCI_DSS_2025_CONTROLS_CATALOG.length, ...breakdown, score };
  }, [statuses]);
  const getTitle = (c: PciDssControl) =>
    locale === 'ru' ? c.title.ru : locale === 'es' ? c.title.es : c.title.en;
  const getDesc = (c: PciDssControl) =>
    locale === 'ru' ? c.description.ru : locale === 'es' ? c.description.es : c.description.en;
  const getRec = (c: PciDssControl) => {
    const r = c.recommendations.CRITICAL || c.recommendations.MAJOR;
    return r ? (locale === 'ru' ? r.ru : locale === 'es' ? r.es : r.en) : null;
  };

  return (
    <div className={`${componentScope('PciDssControlsPanel')} space-y-4`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('compliance.pciDss.controls.title', 'PCI DSS 2025 Controls Catalog')}
          </h3>
          <p className="text-sm text-gray-500">
            {t('compliance.pciDss.controls.subtitle', '6 controls • Network, Data, Access')}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Select<PciDssControl['domain'] | 'all'>
            options={[
              { value: 'all', label: t('compliance.pciDss.controls.allDomains', 'All domains') },
              ...DOMAIN_ORDER.map((d) => ({ value: d, label: d })),
            ]}
            value={filterDomain}
            onChange={(v) => v != null && setFilterDomain(v)}
            placeholder={t('compliance.pciDss.controls.allDomains', 'All domains')}
            minWidth="12rem"
          />
          {onSave && (
            <Button type="button" color="primary" size="small" onClick={handleSave} disabled={disabled || isSaving}>
              {isSaving ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
            </Button>
          )}
        </div>
      </div>
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
        <div className="flex flex-wrap gap-6 text-sm">
          <span>
            <strong>{stats.implemented}</strong>{' '}
            {t('compliance.pciDss.controls.implemented', 'Implemented')}
          </span>
          <span>
            <strong>{stats.partial}</strong>{' '}
            {t('compliance.pciDss.controls.partial', 'Partial')}
          </span>
          <span>
            <strong>{stats.notImplemented}</strong>{' '}
            {t('compliance.pciDss.controls.notImplemented', 'Not Implemented')}
          </span>
          <span>
            <strong>{stats.na}</strong> N/A
          </span>
          <span className="ml-auto font-medium">
            {t('compliance.pciDss.controls.score', 'Score')}: <strong>{stats.score}%</strong>
          </span>
        </div>
      </div>
      <div className="space-y-2">
        {(filterDomain === 'all' ? DOMAIN_ORDER : [filterDomain]).map((domain) => {
          const domainControls =
            filterDomain === 'all' ? getPciDssControlsByDomain(domain) : controlsByDomain;
          const isExpanded = expandedDomains.has(domain);
          return (
            <div key={domain} className="rounded-lg border border-gray-200 dark:border-gray-700">
              {filterDomain === 'all' && (
                <Button
                  type="button"
                  color="outline"
                  onClick={() => toggleDomain(domain)}
                  className="w-full !justify-between rounded-none !border-x-0 !border-t-0 !border-b-gray-200 !bg-transparent !px-4 !py-3 !text-left !font-medium !text-gray-900 !shadow-none dark:!border-b-gray-700 dark:!text-gray-100"
                >
                  <span className="flex items-center gap-2">
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    {domain} ({getPciDssControlsByDomain(domain).length})
                  </span>
                </Button>
              )}
              {(filterDomain !== 'all' || isExpanded) && domainControls.length > 0 && (
                <div className="border-t border-gray-200 dark:border-gray-700">
                  {domainControls.map((control) => (
                    <div
                      key={control.controlCode}
                      className="border-b border-gray-100 last:border-b-0 dark:border-gray-700/50"
                    >
                      <div className="flex flex-wrap items-start gap-4 px-4 py-3">
                        <div className="min-w-0 flex-1">
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            {control.controlCode.replace('PCI_', '')}: {getTitle(control)}
                          </span>
                        </div>
                        <Select<string>
                          options={[
                            { value: '', label: '—' },
                            ...STATUS_OPTIONS.map((s) => ({ value: s, label: STATUS_LABELS[s] })),
                          ]}
                          value={statuses[control.controlCode] ?? ''}
                          onChange={(v) =>
                            handleStatusChange(control.controlCode, (v ?? '') as PciDssControlStatus | '')
                          }
                          disabled={disabled}
                          minWidth="9rem"
                        />
                      </div>
                      {(statuses[control.controlCode] === 'PARTIAL' ||
                        statuses[control.controlCode] === 'NOT_IMPLEMENTED') &&
                        getRec(control) && (
                          <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-2 text-sm dark:border-gray-700/50 dark:bg-gray-800/30">
                            <div className="flex items-start gap-2 rounded-md bg-amber-50 p-2 dark:bg-amber-900/20">
                              <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
                              <p className="text-amber-800 dark:text-amber-200">
                                {getRec(control)}
                              </p>
                            </div>
                          </div>
                        )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
